// ============================================================================
// TournamentCinematic - full-screen moment overlays: FINAL TABLE / IN THE MONEY
// ============================================================================
// Pure CSS cinematics (no emoji, no icon components): a dark vignette rises,
// gold rule lines sweep out from center, the title tracks-in with a moving
// gradient sheen, subtitle fades up, then the whole thing releases. Fired by
// GamePage on the transition (last table break / bubble burst), auto-
// dismisses after ~4.2s.
// ============================================================================
import React, { useEffect } from 'react';

interface TournamentCinematicProps {
  mode: 'finalTable' | 'inTheMoney';
  subtitle?: string;
  onDone: () => void;
}

const DURATION_MS = 4200;

const TournamentCinematic: React.FC<TournamentCinematicProps> = ({ mode, subtitle, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, DURATION_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const title = mode === 'finalTable' ? 'FINAL TABLE' : 'IN THE MONEY';
  const accent = mode === 'finalTable' ? '#d4af37' : '#34d399';
  const accentDeep = mode === 'finalTable' ? '#8a6d1f' : '#0f766e';

  return (
    <div className="pss-cinematic" role="status" aria-live="polite">
      <style>{`
        .pss-cinematic {
          position: fixed; inset: 0; z-index: 9500;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          pointer-events: none;
          background: radial-gradient(ellipse at center, rgba(4, 10, 18, 0.55) 0%, rgba(2, 6, 12, 0.92) 78%);
          animation: pssCinFade ${DURATION_MS}ms ease both;
        }
        @keyframes pssCinFade {
          0% { opacity: 0; }
          7% { opacity: 1; }
          86% { opacity: 1; }
          100% { opacity: 0; }
        }
        .pss-cin-rule {
          width: min(64vw, 560px); height: 2px; position: relative;
          background: linear-gradient(90deg, transparent 0%, ${accent} 18%, #fff7d6 50%, ${accent} 82%, transparent 100%);
          transform: scaleX(0);
          animation: pssCinRule 900ms cubic-bezier(0.16, 1, 0.3, 1) 250ms forwards;
        }
        .pss-cin-rule::after {
          content: ''; position: absolute; left: 50%; top: 50%;
          width: 7px; height: 7px; transform: translate(-50%, -50%) rotate(45deg);
          background: ${accent}; box-shadow: 0 0 12px ${accent};
        }
        @keyframes pssCinRule { to { transform: scaleX(1); } }
        .pss-cin-title {
          margin: 26px 0; padding: 0 12px;
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(38px, 7.5vw, 84px);
          font-weight: 700; line-height: 1; white-space: nowrap;
          letter-spacing: 0.06em;
          background-image: linear-gradient(100deg,
            ${accentDeep} 0%, ${accent} 22%, #fffbe8 38%, ${accent} 52%, ${accentDeep} 74%, ${accent} 100%);
          background-size: 240% 100%;
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
          filter: drop-shadow(0 3px 22px ${accent}55);
          opacity: 0; transform: scale(1.35);
          animation:
            pssCinTitleIn 950ms cubic-bezier(0.16, 1, 0.3, 1) 350ms forwards,
            pssCinSheen 2400ms linear 900ms infinite;
        }
        @keyframes pssCinTitleIn {
          to { opacity: 1; transform: scale(1); letter-spacing: 0.22em; }
        }
        @keyframes pssCinSheen {
          0% { background-position: 120% 0; }
          100% { background-position: -120% 0; }
        }
        .pss-cin-sub {
          font-family: -apple-system, 'Segoe UI', Roboto, sans-serif;
          font-size: clamp(13px, 2vw, 18px);
          color: rgba(226, 232, 240, 0.92);
          letter-spacing: 0.32em; text-transform: uppercase;
          opacity: 0; transform: translateY(10px);
          animation: pssCinSubIn 700ms ease 1150ms forwards;
        }
        @keyframes pssCinSubIn { to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div className="pss-cin-rule" />
      <div className="pss-cin-title">{title}</div>
      <div className="pss-cin-rule" style={{ animationDelay: '350ms' }} />
      {subtitle && <div className="pss-cin-sub" style={{ marginTop: 22 }}>{subtitle}</div>}
    </div>
  );
};

export default TournamentCinematic;
